import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const portfolioProjects = [
  {
    id: 1,
    title: "Executive Leadership Platform",
    client: "Fortune 500 Technology Company",
    category: "Digital Strategy & Development",
    platform: "Custom Development",
    description: "Complete digital transformation including executive dashboard, performance analytics, and automated reporting systems.",
    challenge: "Streamline C-suite decision-making processes and improve organizational transparency",
    solution: "Custom platform with real-time KPI tracking, predictive analytics, and automated executive briefings",
    results: ["40% faster decision-making", "90% reduction in manual reporting", "Enhanced strategic visibility"],
    image: "https://public.readdy.ai/ai/img_res/3e1993208be3dc789cefdebdcaf67baa.jpg",
    tags: ["Digital Strategy", "Custom Development", "Analytics"]
  },
  {
    id: 2,
    title: "Healthcare Innovation Hub",
    client: "Regional Medical Center",
    category: "Website Design & PMO",
    platform: "Webflow",
    description: "Modern healthcare website with patient portal integration and comprehensive PMO implementation.",
    challenge: "Modernize patient experience while implementing robust project management frameworks",
    solution: "Responsive healthcare website with integrated booking system and full PMO structure",
    results: ["60% increase in online appointments", "Streamlined project delivery", "Enhanced patient satisfaction"],
    image: "https://readdy.ai/api/search-image?query=modern%20healthcare%20website%20interface%20with%20clean%20medical%20design%2C%20professional%20medical%20technology%20platform%20with%20soft%20blue%20and%20white%20color%20scheme%2C%20patient-focused%20digital%20experience&width=800&height=600&seq=portfolio2&orientation=landscape",
    tags: ["Healthcare", "Webflow", "PMO Implementation"]
  },
  {
    id: 3,
    title: "Financial Services Optimization",
    client: "Boutique Investment Firm",
    category: "Process Optimization & ISO",
    platform: "Squarespace",
    description: "Complete operational overhaul including ISO compliance, SOP development, and digital presence enhancement.",
    challenge: "Achieve ISO certification while modernizing client-facing operations",
    solution: "Comprehensive process mapping, SOP documentation, and premium website development",
    results: ["ISO 9001 certification achieved", "50% operational efficiency gain", "Premium brand positioning"],
    image: "https://readdy.ai/api/search-image?query=professional%20financial%20services%20website%20with%20luxury%20design%2C%20sophisticated%20investment%20platform%20interface%20with%20gold%20and%20navy%20accents%2C%20executive-level%20financial%20technology%20design&width=800&height=600&seq=portfolio3&orientation=landscape",
    tags: ["ISO Compliance", "Process Optimization", "Financial Services"]
  },
  {
    id: 4,
    title: "Government Digital Transformation",
    client: "State Government Department",
    category: "PMO & Digital Consulting",
    platform: "Custom Solution",
    description: "Large-scale digital transformation project with full PMO leadership and stakeholder management.",
    challenge: "Modernize legacy systems while maintaining security and compliance requirements",
    solution: "Phased transformation approach with comprehensive change management and PMO governance",
    results: ["100% on-time delivery", "Enhanced citizen services", "Improved operational efficiency"],
    image: "https://readdy.ai/api/search-image?query=modern%20government%20digital%20platform%20interface%20with%20professional%20civic%20design%2C%20clean%20administrative%20technology%20system%20with%20secure%20government-style%20interface%20and%20accessibility%20features&width=800&height=600&seq=portfolio4&orientation=landscape",
    tags: ["Government", "Digital Transformation", "PMO Leadership"]
  },
  {
    id: 5,
    title: "E-commerce Growth Platform",
    client: "Luxury Retail Brand",
    category: "Digital Marketing & SEO",
    platform: "Shopify Plus",
    description: "Complete e-commerce optimization including SEO strategy, email marketing automation, and analytics implementation.",
    challenge: "Scale luxury brand online while maintaining premium positioning",
    solution: "Comprehensive digital marketing strategy with advanced analytics and conversion optimization",
    results: ["300% organic traffic increase", "250% conversion rate improvement", "Premium brand positioning maintained"],
    image: "https://readdy.ai/api/search-image?query=luxury%20e-commerce%20website%20interface%20with%20premium%20fashion%20design%2C%20sophisticated%20online%20shopping%20platform%20with%20elegant%20product%20displays%20and%20high-end%20retail%20aesthetics&width=800&height=600&seq=portfolio5&orientation=landscape",
    tags: ["E-commerce", "SEO Strategy", "Digital Marketing"]
  },
  {
    id: 6,
    title: "Tech Startup Launch Strategy",
    client: "AI Technology Startup",
    category: "Complete Digital Strategy",
    platform: "Multi-Platform",
    description: "End-to-end launch strategy including brand development, website creation, and go-to-market planning.",
    challenge: "Launch innovative AI solution with limited budget and tight timeline",
    solution: "Comprehensive launch strategy with phased approach and performance-based metrics",
    results: ["Successful Series A funding", "Market leader positioning", "Rapid user acquisition"],
    image: "https://readdy.ai/api/search-image?query=modern%20technology%20startup%20website%20with%20innovative%20AI%20design%2C%20futuristic%20tech%20platform%20interface%20with%20clean%20professional%20layout%20and%20cutting-edge%20technology%20aesthetics&width=800&height=600&seq=portfolio6&orientation=landscape",
    tags: ["Startup Strategy", "AI Technology", "Go-to-Market"]
  }
];

const categories = ["All Projects", "Digital Strategy", "Website Development", "PMO & Consulting", "Process Optimization"];

export default function Portfolio() {
  const [selectedCategory, setSelectedCategory] = useState("All Projects");
  const [selectedProject, setSelectedProject] = useState<typeof portfolioProjects[0] | null>(null);

  const filteredProjects = selectedCategory === "All Projects" 
    ? portfolioProjects 
    : portfolioProjects.filter(project => 
        project.category.toLowerCase().includes(selectedCategory.toLowerCase().replace(" & ", " ").replace("website development", "design")) ||
        project.tags.some(tag => tag.toLowerCase().includes(selectedCategory.toLowerCase().replace(" & ", " ").replace("website development", "design")))
      );

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-stone-900 mb-6">
              Portfolio & Results
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 font-light leading-relaxed mb-12">
              Proven success stories across digital transformation, PMO leadership, and strategic consulting engagements
            </p>
          </div>
        </div>
      </section>

      {/* Filter Navigation */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-yellow-600 text-white shadow-lg'
                    : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="group cursor-pointer"
                onClick={() => setSelectedProject(project)}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-stone-100 text-stone-600 text-xs font-medium rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-3 group-hover:text-yellow-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-stone-600 mb-4 font-medium">
                      {project.client}
                    </p>
                    <p className="text-stone-500 line-clamp-3 mb-6">
                      {project.description}
                    </p>
                    <div className="flex items-center text-yellow-600 font-medium group-hover:gap-3 transition-all">
                      View Case Study
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-6 right-6 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-600 hover:text-stone-900 hover:bg-white transition-all z-10"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="aspect-[16/9] overflow-hidden rounded-t-2xl">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="p-8">
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedProject.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-stone-100 text-stone-600 text-sm font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="font-['Playfair_Display'] text-4xl font-bold text-stone-900 mb-4">
                {selectedProject.title}
              </h2>
              <p className="text-xl text-stone-600 font-medium mb-2">
                {selectedProject.client}
              </p>
              <p className="text-yellow-600 font-medium mb-8">
                Platform: {selectedProject.platform}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-4">
                    Challenge
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    {selectedProject.challenge}
                  </p>
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-4">
                    Solution
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    {selectedProject.solution}
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-6">
                  Key Results
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {selectedProject.results.map((result, index) => (
                    <div key={index} className="bg-stone-50 p-4 rounded-lg text-center">
                      <p className="font-medium text-stone-900">
                        {result}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-8 border-t border-stone-200">
                <button 
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
                  onClick={() => window.REACT_APP_NAVIGATE('/book-consultation')}
                >
                  Start Similar Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-xl text-stone-300 mb-12 max-w-2xl mx-auto">
            Let's discuss how we can deliver similar results for your organization through strategic consulting and proven methodologies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/book-consultation')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
            >
              Book Strategy Session
            </button>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/services')}
              className="border-2 border-white text-white hover:bg-white hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
            >
              Explore Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}